import { Dialog, DialogActions, DialogContent, FormGroup, Grid, Stack } from "@mui/material";
import { MprcChipChoice, MprcContainer, MprcDialogTitleClose, MprcRequestErrorReport } from "components";
import { ButtonCancel, ButtonConfirm } from "components/buttons";
import { ChannelAutoCompleteDataModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { TopicAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { MprcDatePicker } from "components/MprcDatePicker";
import { KnowledgeQuickFilterType, ManagedQuickFilterResponse, ManagedQuickFilterTypeListResponse, StickerAppliesTo } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { StickerAutoComplete } from "views/foundation/sticker/components/StickerAutoComplete";
import { TopicAutoCompleteHierarchical } from "views/foundation/topic/components/TopicAutoCompleteHierarchical";
import { ManagedChannelAutoComplete } from "views/knowledge/channels/components/ManagedChannelAutoComplete";


////////////////////////////////////////////////////////////////////////////////

interface IProps {
    record: ManagedQuickFilterResponse;
    filterTypes: ManagedQuickFilterTypeListResponse[];
    onClose: () => void;
    onConfirm: (record: ManagedQuickFilterResponse) => void;
}


////////////////////////////////////////////////////////////////////////////////

export const EditQuickFilter: React.FC<IProps> = (props) => {
    ////////////////////////////////////////////////////////////////////////////////

    const { filterTypes, onConfirm, onClose } = props;

    const [viewData, setViewData] = useState<ManagedQuickFilterResponse>(props.record);
    const loadDto = useRef<ManagedQuickFilterResponse>(LibUtils.deepCopy(viewData));
    const [canSave, setCanSave] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null!);
    const { t } = useTranslation("knowledge");

    ///
    function handleUpdateRecord(name: keyof ManagedQuickFilterResponse, v: any) {
        setViewData({ ...viewData, [name]: v });
    }

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto.current);
        const isValid = (viewData.filterType == KnowledgeQuickFilterType.CustomTopic && viewData.topic.length > 0)
            || (viewData.filterType == KnowledgeQuickFilterType.CustomChannel && viewData.channel.length > 0)
            || (viewData.filterType == KnowledgeQuickFilterType.CustomSticker && viewData.sticker.length > 0);
        setCanSave(isRecordChanged && isValid);
    }, [viewData]);

    return (

        <Dialog
            open
            fullWidth
            maxWidth="md"
            onClose={() => onClose()}
        >
            <MprcDialogTitleClose
                shrinkPadding
                title={`${viewData.quickFilterId == ""
                    ? t("QuickFilter.Edit.NewText")
                    : t("QuickFilter.Edit.EditText")} ${t("QuickFilter.Edit.Title")}`
                }
                subtitle={t("QuickFilter.Edit.Subtitle")}
                onClose={() => onClose()}
            />

            <DialogContent>
                <MprcContainer>
                    <MprcRequestErrorReport error={viewError} />
                    <FormGroup>
                        <Stack direction="row">
                            {filterTypes.map(x =>
                                viewData.quickFilterId == "" || x.filterType == viewData.filterType
                                    ?
                                    <MprcChipChoice
                                        label={x.name}
                                        key={x.filterType}
                                        selected={x.filterType == viewData.filterType}
                                        onClick={() => handleUpdateRecord("filterType", x.filterType)}
                                    />
                                    :
                                    null
                            )}
                        </Stack>
                        <Grid container mt={1}>
                            {
                                viewData.filterType == KnowledgeQuickFilterType.CustomTopic &&
                                <TopicAutoCompleteHierarchical
                                    selectedMode="free"
                                    label={t("QuickFilter.Edit.TopicLabel")}
                                    multiSelect={false}
                                    disabled={viewData.quickFilterId != ""}
                                    selectedItems={viewData.topic as TopicAutoCompleteHierarchicalModel[]}
                                    onChange={(items) => handleUpdateRecord("topic", items)}
                                    onError={error => setViewError(error)}
                                />
                            }
                            {
                                viewData.filterType == KnowledgeQuickFilterType.CustomChannel &&
                                <ManagedChannelAutoComplete
                                    label={t("QuickFilter.Edit.ChannelLabel")}
                                    multiSelect={false}
                                    disabled={viewData.quickFilterId != ""}
                                    selectedItems={viewData.channel as ChannelAutoCompleteDataModel[]}
                                    onChange={(items) => handleUpdateRecord("channel", items)}
                                    onError={error => setViewError(error)}
                                />
                            }
                            {
                                viewData.filterType == KnowledgeQuickFilterType.CustomSticker &&
                                <StickerAutoComplete
                                    checkRestrictions
                                    applyTo={StickerAppliesTo.Publication}
                                    label={t("QuickFilter.Edit.StickerLabel")}
                                    multiSelect={false}
                                    disabled={viewData.quickFilterId != ""}
                                    selectedItems={viewData.sticker}
                                    onChange={(items) => handleUpdateRecord("sticker", items)}
                                    onError={error => setViewError(error)}
                                />
                            }
                        </Grid>

                        <Grid container mt={2}>
                            <Grid>
                                <MprcDatePicker
                                    width="200px"
                                    label={t("QuickFilter.Edit.DueDateLabel")}
                                    value={viewData.dueDate}
                                    placeholder={t("QuickFilter.Edit.DueDatePlaceholder")}
                                    onChange={(date) => handleUpdateRecord("dueDate", date)}
                                />
                            </Grid>
                        </Grid>
                    </FormGroup>
                </MprcContainer>

            </DialogContent>

            <DialogActions>
                <ButtonCancel variant="contained" onClick={onClose} />
                <ButtonConfirm
                    disabled={!canSave}
                    variant="contained"
                    onClick={() => onConfirm(viewData)}
                />
            </DialogActions>
        </Dialog>
    );
};
